import { Markets } from "../../components/Lit/acc-es-data/model";
import { Config } from "../../common/interfaces/config";
import { ImpersonationDetails, AuthConfigs } from "../../common/interfaces/impersonation";
import { fetchConfig } from "../../common/util/config";
import { store } from "../../store";
import { setUser } from "../../reducers/boot";
import { SetLoginFields } from "../../reducers/model/boot";
import { clearSession } from "../../common/util/cookies";
import { launchEntraImpersonation } from "./entra-util";

export const setProgress = (progress: number) => {
  const progressBar = document.querySelector(".impersonation__bar") as HTMLElement;
  if (progressBar) {
    progressBar.style.width = `${progress}%`;
  }
};

export const loadAmwayIdAuthConfigs = (defaultConfig: Config): AuthConfigs => {
  return {
    UrlBase: defaultConfig.amwayIdAuthUrlBase!,
    AuthServer: defaultConfig.amwayIdAuthServer!,
    ClientId: defaultConfig.amwayIdClientId!,
    RedirectUri: defaultConfig.cppModAmwayIdRedirectUri!,
    IdpDomain: defaultConfig.amwayIdIdpDomain!,
    UseNewOktaTokenApi: defaultConfig.useNewOktaTokenApi ?? false,
    OktaTokenApiUrlBase: defaultConfig.apiBasePaths?.oktaToken
      ? defaultConfig.apiBaseUrl + defaultConfig.apiBasePaths.oktaToken
      : "",
    UseGluuAuth: defaultConfig.useGluuAuth,
    GluuTokenUrl: defaultConfig.gluuTokenUrl,
  };
};

export const parseJwt = (token: string) => {
  // eslint-disable-next-line no-useless-catch
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );

    return JSON.parse(jsonPayload);
  } catch (e) {
    throw e;
  }
};

export const loadOktaAuthConfigs = (defaultConfig: Config): AuthConfigs => {
  return {
    UrlBase: defaultConfig.oktaUrlBase!,
    AuthServer: defaultConfig.oktaAuthServerId!,
    ClientId: defaultConfig.oktaClientId!,
    RedirectUri: defaultConfig.cppModOktaRedirectUri!,
    IdpDomain: defaultConfig?.impersonationOkta?.idpDomain!,
    UseNewOktaTokenApi: defaultConfig.useNewOktaTokenApi ?? false,
    OktaTokenApiUrlBase: defaultConfig.apiBasePaths?.oktaToken
      ? defaultConfig.apiBaseUrl + defaultConfig.apiBasePaths.oktaToken
      : "",
  };
};

export const parseHash = (hash: string) => {
  return hash
    .substring(1)
    .split("&")
    .map((i) => i.split("="))
    .reduce((acc: any, cur: any) => {
      acc[cur[0]] = cur[1] || "";
      return acc;
    }, {});
};

export const normalizeLocale = (locale: string | null) => {
  if (locale) {
    // below logic is needed as there is no content for es-co. so assigning default es-es locale for content
    if (locale === "es-co") {
      locale = "es-es";
    }
    const parts = locale.split("-");
    if (parts[0] && parts[1]) {
      locale = parts[0].toLowerCase() + "-" + parts[1].toUpperCase();
    }
  }
  return locale;
};

export const normalizeSsoOrigin = (ssOrigin: string | null) => {
  return ssOrigin ? ssOrigin.toUpperCase() : ssOrigin;
};

// ssoOrigin can be a compound value like "MX_NG" (market + sub-market),
// but X-Amway-Country only needs the ISO country code (e.g. "MX").
// We split on "_" and take the first part to handle both "MX" and "MX_NG" cases.
export const getXAmwayCountry = (ssoOrigin: string) => (ssoOrigin.length > 2 ? ssoOrigin.split("_")[0] : ssoOrigin);

export const getLambdaEnvironment = (env: string) => {
  switch (env.toLowerCase()) {
    case "test":
    case "qa":
    default:
      return "preprod";
    case "prod":
      return "prod";
  }
};

export const parseScope = (scope: any) => {
  return decodeURIComponent(scope)
    .split("+")
    .map((i) => i.split("="))
    .reduce((acc: any, cur: any) => {
      acc[cur[0]] = cur[1] || "";
      return acc;
    }, {});
};

export const padAff = (aff: string) => {
  return `${"0".repeat(3 - aff.toString().length)}${aff.toString()}`;
};

export const redirectUnavailable = (navigate: Function) => {
  navigate("/error/pageUnavailable");
};

export const redirectRestricted = (navigate: Function) => {
  navigate("/error/userRestriction");
};

export const redirectAuthFailed = (navigate: Function) => {
  navigate("/error/authenticationFailed");
};

export const redirectComingSoon = (navigate: Function) => {
  navigate("/error/genericError");
};

export const redirectBusinessSelector = (businessList: any, navigate: Function) => {
  const businesses = encodeURIComponent(JSON.stringify(businessList));
  navigate(`/business-selector?businesses=${businesses}`);
};

export const redirectLocaleFailed = (navigate: Function) => {
  navigate("/error/invalidLocale");
};

export const redirectAccountFailed = (navigate: Function) => {
  navigate("/error/incorrectAccount");
};

export const affiliates = new Markets().list.map((m) => ({
  country: m.ctry,
  spcode: m.aff,
  label: m.name,
  iso: m.iso,
}));

export const createAmwayPlusImpersonationURL = async (
  impersonationDetails: ImpersonationDetails,
): Promise<string | void> => {
  const { abo, locale, country: aff } = impersonationDetails;

  try {
    const market = new Markets().getByCtry(aff);
    const config = await fetchConfig(`/configs/${market.iso}.configuration.json`);
    return `${config.amwayPlusImpersonationDomain}/impersonation?aff=${aff}&abo=${abo}&countryCode=${market.iso}&locale=${locale}`;
  } catch (error) {
    console.log("Error fetching config - configuration.json", error);
  }
};

export const createAcademyImpersonationURL = async (
  impersonationDetails: ImpersonationDetails,
): Promise<string | void> => {
  const { abo, partyId, locale, country: aff } = impersonationDetails;

  try {
    const market = new Markets().getByCtry(aff);
    const config = await fetchConfig(`/configs/${market.iso}.configuration.json`);
    const spcode = affiliates.find((affiliate) => parseInt(affiliate.country) === parseInt(aff))?.spcode;

    if (!spcode) {
      console.error(`No spcode found for country "${aff}"`);
      return;
    }

    const baseUrl = config.academyImpersonationDomain;
    if (!baseUrl) {
      console.error("Academy impersonation domain not configured");
      return;
    }

    // Locale parameter is optional according to JIRA comment
    const localeParam = locale ? `&locale=${locale.toLowerCase()}` : "";
    return `${baseUrl}/impersonate?country=${market.iso}&salesPlanAff=${padAff(spcode)}&aboNumber=${abo}&partyId=${partyId || ""}${localeParam}`;
  } catch (error) {
    console.log("Error fetching config - configuration.json", error);
  }
};

export const authorization = async (impersonationDetails: ImpersonationDetails) => {
  const { abo, partyId, locale, country, sameWindow, entryPage, targetAbo, startingPeriod } = impersonationDetails;

  // Resolve the affiliate for the requested country BEFORE touching any session
  // state. If the country doesn't map to a known affiliate, bail out without
  // clearing — otherwise we'd wipe the current session and then crash on the
  // undefined affiliate.
  const aff = affiliates.find((aff) => parseInt(aff.country) === parseInt(country));
  if (!aff) {
    console.error(`Impersonation aborted: no affiliate found for country "${country}".`);
    return;
  }

  // Pre-flight the TARGET market's config BEFORE tearing down the current
  // session. fetchConfig() defaults to the path derived from the *current*
  // ssoOrigin, so we pass the resolved affiliate's path explicitly to validate
  // the market we're about to log into (not the prior session's). If it can't
  // be loaded (network failure, or a non-OK response that fetchConfig surfaces
  // as an Error), bail out without clearing — otherwise a failed pre-flight
  // would wipe the existing session and never redirect, leaving the user on a
  // blank, session-less page.
  const config = await fetchConfig(`/configs/${aff.iso}.configuration.json`).catch(() => null);
  if (!config || config instanceof Error) {
    console.error("Impersonation aborted: configuration could not be loaded.");
    return;
  }

  // Clear any prior session at the start of every login so a stale session
  // (in-memory boot.user + cookie + persisted boot.user) from a previous
  // successful login can't be restored after a failed login. loginFields
  // persist to localStorage and are preserved, so the fresh fields set below
  // survive a same-window navigation.
  clearSession();

  SetLoginFields({ fields: { ssoOrigin: aff.iso } });
  store.dispatch(setUser({ user: { isoCountryCode: aff.iso } }));

  if (config.useEntra) {
    launchEntraImpersonation({ abo, spcode: aff.spcode, locale });
  } else if (partyId) {
    // Okta flow - will be removed soon
    impersonateWithJwtAuth(abo, aff.spcode, partyId, locale, sameWindow!, entryPage, targetAbo, startingPeriod);
  }
  // Mashery auth
  else {
    impersonateWithMasheryAuth(abo, aff.spcode, locale, sameWindow!, entryPage, targetAbo, startingPeriod);
  }
};

/**
 * Kicks off the impersonation process with Mashery authentication
 * @param {number} abo
 * @param {string} spcode
 */
async function impersonateWithMasheryAuth(
  abo: string,
  spcode: string,
  locale: string,
  sameWindow: boolean,
  entryPage: string | undefined,
  targetAbo: string | undefined,
  startingPeriod?: string,
) {
  // not removing local storage here as this has to be taken out in another ticket abot-4332 and need some regression testing as well.
  // Load the configuration file
  await fetchConfig().then((config) => {
    const crypto = (window as any).crypto || (window as any).msCrypto;

    const generateSecureRandomNumber = () => {
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      return array[0];
    };

    const nonce = ((Date.now() * generateSecureRandomNumber()) % Number.MAX_SAFE_INTEGER).toString();

    // Save some website params to localStorage
    // so they are persisted after auth
    SetLoginFields({
      fields: {
        locale: locale,
        loginOrigin: "impersonation",
        entryPage: entryPage,
        startingPeriod: startingPeriod,
        targetAbo: targetAbo,
      },
    });

    const local = window.location.href.startsWith("http://localhost");
    const localJwtTokenReceiverUrl = "http://localhost:8000/token.html";
    const oktaRedirectUri = local ? localJwtTokenReceiverUrl : config.cppModTokenReceiverUri;

    const urlToSubmit = `${config.requestUrlBase}${config.apiAuthorizeURL}?response_type=token&client_id=${config.impersonationApiClientId}&scope=aboNum%3d${abo}+salesPlanAff%3d${spcode}&redirect_uri=${oktaRedirectUri}&nonce=${nonce}`;
    window.open(urlToSubmit, sameWindow ? "_self" : "_blank");
    // refreshRecentlyUsed();
  });
}

/**
 * Kicks off the impersonation process with JWT authentication
 */
async function impersonateWithJwtAuth(
  abo: string,
  spcode: string,
  partyId: string,
  locale: string,
  sameWindow: boolean,
  entryPage: string | undefined,
  targetAbo: string | undefined,
  startingPeriod?: string,
) {
  // We need to determine which Okta instance to call.
  // To do that, we need to determine the right market config file to load.
  // To do that, we need to know this user's ISO country code.
  // We can't get that info... but for this porpose, we can use
  // the ISO country code of the user's affiliate's primary market.

  // Temporarily store this in localStorage so our utility functions will use it.
  // This will be overwitten by the jwt receiver.
  try {
    await fetchConfig().then((config) => {
      // Save some website params to localStorage
      // so they are persisted after auth
      SetLoginFields({
        fields: {
          locale: locale,
          loginOrigin: "impersonation",
          entryPage: entryPage,
          targetAbo: targetAbo,
          startingPeriod: startingPeriod,
        },
      });

      const state = {
        aboNumber: abo,
        salesPlanAff: spcode,
        partyId: partyId,
      };
      const encodedState = window.btoa(JSON.stringify(state));

      const local = window.location.href.startsWith("http://localhost");
      const localJwtTokenReceiverUrl = "http://localhost:8000/jwt-receiver.html";
      const oktaRedirectUri = local ? localJwtTokenReceiverUrl : config.cppModOktaRedirectUri;

      const urlToSubmit = `${config.oktaUrlBase}${config.oktaAuthServer}?client_id=${config.oktaClientId}&response_type=code&scope=${config.impersonationOkta.oktaScope}&redirect_uri=${oktaRedirectUri}&state=${encodedState}`;
      window.open(urlToSubmit, sameWindow ? "_self" : "_blank");
    });
  } catch (error) {
    console.log("Error fetching config - configuration.json");
  }
}

export const isGatewayError = (e: { message: string }) =>
  e?.message && (e?.message.includes("504") || e?.message.includes("502"));
